import React, { useState } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { CountrySuggest } from "Components/Form/CountrySuggest";
import Input from "../../../../../../../../../components/Form/Input";
import Select from "../../../../../../../../../components/Form/Select";
import SkeletonComponent from "../../../../../../../../../components/Generic/Skeleton";
import { useAppState } from "../../../../../../../../../context/StateContext";
import { useLanguageHook } from "../../../../../../../../../hooks/useLanguageHook";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { Country } from "../../../../../../../../../models/country";
import { Languages as LanguagesType } from "../../../../../../../../../models/languages";
import LanguagesService from "../../../../../../../../../services/languages.service";
import useStyles from "./styles";

interface LanguageFormProps {
  isOnEditModal?: boolean;
  isLoading: boolean;
  errors: yup.ValidationError[];
}

const LanguageForm = ({ isOnEditModal, isLoading, errors }: LanguageFormProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const { language, handleUpdateLanguage } = useLanguageHook();

  const [languages, setLanguages] = useState<LanguagesType[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { isFetching: isLoadingLanguages } = useQuery(
    ["languages.modal.form.list"],
    () =>
      LanguagesService.getAll({
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response: AxiosResponse<LanguagesType[]>) => {
        if (response && response.data) setLanguages(response.data);
      },
    }
  );

  const { isFetching: isLoadingLanguagesCodes, data: prefixes } = useQuery(
    ["languages.get.all.codes"],
    async () => {
      const response = await LanguagesService.getAllPrefixes();

      if (response && response.data && response.data instanceof Array) return response?.data;

      return [];
    },
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
    }
  );

  return (
    <section className={classes.modalSection}>
      <Grid container direction="row" alignItems="flex-start" spacing={2}>
        <Grid item md={12} lg={12}>
          <SkeletonComponent isLoading={isLoadingLanguages && !isOnEditModal}>
            <Select
              disabled={isLoading || isLoadingLanguages || isOnEditModal}
              name="baseLanguage"
              label={translate("baseLanguage")}
              required
              error={fieldHasError("baseLanguage")}
              errorMessage={getErrorByField("baseLanguage")}
              value={language?.baseLanguageId}
              dataCy="baseLanguageSelect"
              onChange={(event: SelectChangeEvent<any>) => {
                handleUpdateLanguage("baseLanguageId", event.target.value);
                const baseLanguage = languages.find(
                  (lang) => lang.id === Number(event.target.value)
                );

                if (baseLanguage) {
                  handleUpdateLanguage(
                    "defaultLanguageId",
                    baseLanguage.defaultLanguageId || baseLanguage.id
                  );
                }
              }}
            >
              {languages &&
                languages.map((languageValue, index) => (
                  <MenuItem
                    key={languageValue.id}
                    value={languageValue.id}
                    data-cy={`baseLanguageMenu-${index}`}
                  >
                    {languageValue.name}
                  </MenuItem>
                ))}
            </Select>
          </SkeletonComponent>
        </Grid>
        <Grid item md={6} lg={6}>
          <Select
            disabled={isLoading || isLoadingLanguagesCodes}
            name="languagePrefix"
            required
            label={translate("prefix")}
            dataCy="languagePrefixInput"
            error={fieldHasError("languagePrefix")}
            errorMessage={getErrorByField("languagePrefix")}
            value={language?.prefix}
            onChange={(event: SelectChangeEvent<any>) =>
              handleUpdateLanguage("prefix", event.target.value)
            }
          >
            {prefixes && prefixes?.length > 0
              ? prefixes.map((prefix) => {
                  if (prefix?.code) {
                    return (
                      <MenuItem key={prefix?.code} value={prefix?.code}>
                        {prefix?.language} - {prefix?.code}
                      </MenuItem>
                    );
                  }

                  return null;
                })
              : null}
          </Select>
        </Grid>
        <Grid item md={6} lg={6}>
          <Input
            disabled={isLoading}
            name="languageName"
            required
            data-cy="languageNameInput"
            label={translate("languageName")}
            placeholder={translate("australianEnglishSystem")}
            error={fieldHasError("languageName")}
            errorMessage={getErrorByField("languageName")}
            value={language?.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleUpdateLanguage("name", event.target.value)
            }
          />
        </Grid>
        <Grid item md={6} lg={6} alignItems="center">
          <Grid item md={12} lg={12}>
            <CountrySuggest
              required
              label={translate("flag")}
              onChange={(country) => {
                if (country) handleUpdateLanguage("flag", country?.code);
              }}
              country={{ code: language?.flag } as Country}
              error={fieldHasError("languageFlag")}
              errorMessage={getErrorByField("languageFlag")}
              disabled={isLoading}
            />
          </Grid>
        </Grid>
        <Grid item md={6} lg={6}>
          <Select
            disabled={isLoading || isOnEditModal}
            name="languageUsage"
            required
            label={"Usage"}
            dataCy="languageUsageSelect"
            error={fieldHasError("languageUsage")}
            errorMessage={getErrorByField("languageUsage")}
            value={language?.usage}
            onChange={(event: SelectChangeEvent<any>) =>
              handleUpdateLanguage("usage", event.target.value)
            }
          >
            <MenuItem value="Web" data-cy="languageUsageMenu-Web">
              {translate("web")}
            </MenuItem>
            <MenuItem value="App" data-cy="languageUsageMenu-App">
              {translate("app")}
            </MenuItem>
          </Select>
        </Grid>
      </Grid>
    </section>
  );
};

export default LanguageForm;
